import { normalizeUsername } from './usernameUtils';
export const PROFILE_ROLE_SEGMENTS = ['creator', 'collector', 'seller', 'curator'] as const;
export type ProfileRoleSegment = (typeof PROFILE_ROLE_SEGMENTS)[number];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

export function isProfileRoleSegment(value: string): value is ProfileRoleSegment {
  return (PROFILE_ROLE_SEGMENTS as readonly string[]).includes(value);
}

export function roleSegmentFromAccountType(
  accountType: string | null | undefined
): ProfileRoleSegment {
  switch (accountType) {
    case 'seller':
      return 'seller';
    case 'curator':
      return 'curator';
    case 'creator':
      return 'creator';
    case 'collector':
    default:
      return 'collector';
  }
}

/** Public profile URL: /{role}/{username} e.g. /creator/kickz_lord */
export function profilePathFromParts(
  accountType: string | null | undefined,
  username: string
): string {
  const role = roleSegmentFromAccountType(accountType);
  const uname = normalizeUsername(username);
  return `/${role}/${encodeURIComponent(uname)}`;
}

type ProfilePathInput = {
  username: string;
  account_type?: string | null;
  accountType?: string | null;
};

function accountTypeOf(p: { account_type?: string | null; accountType?: string | null }): string | null | undefined {
  return p.account_type ?? p.accountType;
}

export function profilePathFromProfile(profile: ProfilePathInput): string {
  return profilePathFromParts(accountTypeOf(profile), profile.username);
}

/** When only id is known, uses role/username if present; otherwise /creator/{id} (redirects after load). */
export function profilePublicPath(p: {
  id: string;
  username?: string | null;
  account_type?: string | null;
  accountType?: string | null;
}): string {
  if (p.username) {
    return profilePathFromParts(accountTypeOf(p), p.username);
  }
  return `/creator/${encodeURIComponent(p.id)}`;
}

export function isPublicProfilePath(pathname: string): boolean {
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ? isProfileRoleSegment(seg) : false;
}
